import type { DocPage } from '../types'
import { media } from '../media'


export const profiles: DocPage = {
  id: 'profiles',
  title: 'Profiles & history',
  description:
    'Save and manage Transfer Profiles (save, update, rename, remove, unsaved switch, save as new, associated configs) and use Transfer History columns, filters, logs, and delete permissions.',
  category: 'Transfer',
  sections: [
    { id: 'profiles', title: 'Transfer Profiles' },
    { id: 'save-update', title: 'Save, update, save as new' },
    { id: 'rename-remove', title: 'Rename and remove' },
    { id: 'unsaved', title: 'Unsaved switch' },
    { id: 'associated', title: 'Associated configs' },
    { id: 'history', title: 'Transfer History' },
    { id: 'history-columns', title: 'History columns' },
    { id: 'history-filters', title: 'Filters, logs, delete' },
    { id: 'permission', title: 'History permission' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'profiles',
      text: 'Transfer Profiles',
    },
    {
      type: 'paragraph',
      text: 'A Transfer Profile bundles source configuration, target configuration, mapping, and related settings such as coordinate conversion so you can reload a complete transfer setup from the header Transfer Profiles dropdown.',
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'Screenshot tip',
      text: 'Capture the Transfer Profiles dropdown and Save transfer profile modal as Transfer-Profiles.png to illustrate this section.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'save-update',
      text: 'Save, update, save as new',
    },
    {
      type: 'table',
      headers: ['Action', 'UI label', 'Behavior'],
      rows: [
        [
          'Create',
          'Save Profile',
          'Name a new profile from the current Source/Target/Mapping workspace.',
        ],
        [
          'Overwrite',
          'Update Profile / Save Profile',
          'Write the current workspace into the loaded profile.',
        ],
        [
          'Copy',
          'Save as new transfer profile',
          'Create another profile name without replacing the original.',
        ],
      ],
    },
    {
      type: 'paragraph',
      text: 'The save UI surfaces fields for Source / Target / Mapping / Format / Config / Type so you can confirm what is being packaged.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'rename-remove',
      text: 'Rename and remove',
    },
    {
      type: 'list',
      items: [
        'Rename Profile — change the display name of an existing profile.',
        'Remove Profile — delete a profile you no longer need (confirm when prompted).',
        'Removing a profile does not delete the underlying Model Configurations unless you delete those separately.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'unsaved',
      text: 'Unsaved switch',
    },
    {
      type: 'paragraph',
      text: 'Toolbar/header chrome indicates when the workspace differs from the loaded profile. When switching profiles with unsaved changes, choose Discard or Save and continue / save-as-new as prompted so you do not lose mapping work.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'associated',
      text: 'Associated configs',
    },
    {
      type: 'list',
      items: [
        'Profiles reference Source and Target Model Configurations when those were saved.',
        'When saving a profile you may optionally save source/target configs in the same flow.',
        'If a referenced config was deleted, reload may fail or partially restore — recreate the config or update the profile.',
        'Keep config names stable when sharing profiles across a team.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'history',
      text: 'Transfer History',
    },
    {
      type: 'paragraph',
      text: 'Transfer History lists past runs so you can audit outcomes and download logs. Open it from the horizontal toolbar. Feature-gated by view-transfer-history.',
    },
    {
      type: 'figure',
      src: media.transferHistory,
      alt: 'Transfer History table',
      caption:
        'Figure: Transfer History — search/filters and columns such as ID, Created on, Source, Target, Type, Status, Records, Duration.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'history-columns',
      text: 'History columns',
    },
    {
      type: 'table',
      headers: ['Column', 'Contents'],
      rows: [
        ['Select', 'Row selection for bulk actions when offered.'],
        ['ID', 'Transfer run identifier.'],
        ['Created on', 'Timestamp of the run.'],
        ['Source', 'Source format / summary.'],
        ['Target', 'Target format / summary.'],
        ['Type', 'MAPPED or SCRATCH.'],
        ['Status', 'Success / failure / other run status.'],
        ['Records', 'Record counts transferred.'],
        ['Duration', 'Elapsed time.'],
        ['Summary', 'Short outcome summary.'],
        ['Actions', 'View Logs, Download Logs, Delete Record, and similar.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'history-filters',
      text: 'Filters, logs, delete',
    },
    {
      type: 'figure',
      src: media.transferViewLog,
      alt: 'Transfer View Log dialog',
      caption:
        'Figure: View Logs from Transfer History — inspect the detailed log for a selected run, or download logs for sharing.',
    },
    {
      type: 'list',
      items: [
        'Filter by source/target/mapping type.',
        'Type filters include MAPPED and SCRATCH.',
        'Search transfers... for free-text search.',
        'View Logs / Download Logs for diagnostics.',
        'Delete Record removes history rows you no longer need.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'permission',
      text: 'History permission',
    },
    {
      type: 'callout',
      variant: 'info',
      title: 'Crown / lock',
      text: 'If Transfer History shows a crown or is disabled, your organization entitlement does not include view-transfer-history. Contact your administrator; billing changes are made in SEPM My Account, not inside X-Translator.',
    },
  ],
}
